import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import GameLogosForm from "../tileDialog/GameLogosForm";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { myExpariencesService } from "../../../services";
import {
  addElement,
  updateElement,
} from "../../../reducers/configurator/configuratorSlice";

const PREFIX = "pickleballCustom";

const classes = {
  baseContainer: `${PREFIX}-baseContainer`,
  contentContainer: `${PREFIX}-contentContainer`,
};

const PickleballCustomContainer = styled("div")(({ theme }) => ({
  [`& .${classes.baseContainer}`]: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    padding: "0 1rem",
    marginBottom: "1rem",
  },
  [`& .${classes.contentContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "130px",
    justifyContent: "space-between",
  },
}));

// Filet pickleball parametrable /
// logos sur filet pas Wilson, logo poteau, logo bande /
// selection de raquette /
// lanceur par défaut (possibilité add logo), lanceur champagne

const PickleballCustom = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [gameOptions, setGameOptions] = useState({
    nets: null,
    rackets: null,
    launchers: null,
  });
  const elementsHasBeenFetched = useRef(false);

  const currentTile = useSelector((state) => state.configurator.selectedTile);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const myTile = useSelector((state) =>
    currentTile
      ? state.configurator.grid[parentLayout][currentTile.row][
          currentTile.column
        ]
      : null
  );

  const formatBallObject = (obj, key) => {
    const tmp = { ...obj };
    tmp[`${key}_id`] = tmp._id;
    tmp[`${key}_title`] = tmp.title;
    delete tmp._id;
    delete tmp.title;
    delete tmp.category;
    return tmp;
  };

  useEffect(() => {
    if (!elementsHasBeenFetched.current) {
      elementsHasBeenFetched.current = true;
      Promise.all([
        myExpariencesService.fetchGameTypes("rackets", "pickleball"),
        myExpariencesService.fetchGameTypes("nets", "pickleball"),
        myExpariencesService.fetchGameTypes("launchers", "pickleball"),
      ])
        .then((res) => {
          const myKeys = ["racket", "net", "launcher"];
          setGameOptions({ rackets: res[0], nets: res[1], launchers: res[2] });
          let tmp = {};
          res.forEach((result, index) => {
            const selected = !myTile.middle.hasOwnProperty(
              `${myKeys[index]}_id`
            )
              ? formatBallObject(result[0], myKeys[index])
              : formatBallObject(
                  result.find(
                    (chosen) =>
                      myTile.middle[`${myKeys[index]}_id`] === chosen._id
                  ),
                  myKeys[index]
                );
            tmp = { ...tmp, ...selected };
          });
          dispatch(
            addElement({
              layer: "middle",
              data: {
                ...myTile.middle,
                ...tmp,
              },
              tile: { row: myTile.row, column: myTile.column },
            })
          );
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.fetchGameBallsError"), {
              variant: "error",
            });
        });
    }
  }, [dispatch, enqueueSnackbar, gameOptions, myTile, t]);

  const handleElementChange = (e, myKey) => {
    dispatch(
      updateElement({
        layer: "middle",
        data: {
          ...myTile.middle,
          ...formatBallObject(
            gameOptions[`${myKey}s`].find(
              (opt) => opt.title === e.target.value
            ),
            myKey
          ),
        },
        tile: { row: currentTile.row, column: currentTile.column },
      })
    );
  };

  return (
    <PickleballCustomContainer>
      <ElementSelector
        elements={gameOptions.nets}
        t={t}
        tradIdentifier={"net"}
        myTile={myTile}
        onChange={(e) => handleElementChange(e, "net")}
      />
      <ElementSelector
        elements={gameOptions.rackets}
        t={t}
        tradIdentifier={"racket"}
        myTile={myTile}
        onChange={(e) => handleElementChange(e, "racket")}
      />
      <ElementSelector
        elements={gameOptions.launchers}
        t={t}
        tradIdentifier={"launcher"}
        myTile={myTile}
        onChange={(e) => handleElementChange(e, "launcher")}
      />
      <div className={classes.baseContainer}>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.netLogo")}
          </Typography>
          <GameLogosForm logoKey={"netLogo"} />
        </div>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.netBandLogo")}
          </Typography>
          <GameLogosForm logoKey={"netBandLogo"} />
        </div>
      </div>
      <div className={classes.baseContainer}>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.netPoleLogo")}
          </Typography>
          <GameLogosForm logoKey={"netPoleLogo"} />
        </div>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.pickleballLauncherLogo")}
          </Typography>
          <GameLogosForm logoKey={"launcherLogo"} />
        </div>
      </div>
      <div className={classes.baseContainer}>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.basketGroundLogo")}
          </Typography>
          <GameLogosForm logoKey={"groundLogo"} />
        </div>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.courtLogo")}
          </Typography>
          <GameLogosForm logoKey={"courtLogo"} isPickleball />
        </div>
      </div>
    </PickleballCustomContainer>
  );
};

export default PickleballCustom;

const ElementSelector = ({ elements, t, tradIdentifier, myTile, onChange }) => {
  if (elements) {
    return (
      <div className={classes.baseContainer}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography variant="body1" sx={{ marginBottom: "0.5rem" }}>
            {t(`myExpariences.form.${tradIdentifier}Choice`)}
          </Typography>
          <Box
            sx={{
              width: "100%",
              maxWidth: "250px",
              margin: "0 auto",
              marginBottom: "1rem",
            }}
          >
            <FormControl fullWidth>
              <InputLabel id={`${tradIdentifier}-choice`}>
                {t(`myExpariences.form.${tradIdentifier}Title`)}
              </InputLabel>
              <Select
                labelId={`${tradIdentifier}-choice`}
                id={`${tradIdentifier}-choice`}
                value={myTile.middle[`${tradIdentifier}_title`] || ""}
                renderValue={(v) => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={myTile.middle[`${tradIdentifier}_preview`]}
                      alt="preview element 1"
                      style={{
                        width: "30px",
                        height: "30px",
                        transform: "translateY(-2px)",
                        marginRight: "1.5rem",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        maxWidth: "130px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {v}
                    </Typography>
                  </Box>
                )}
                label={t(`myExpariences.form.${tradIdentifier}Title`)}
                onChange={onChange}
              >
                {elements.map((elem, index) => (
                  <MenuItem
                    value={elem.title}
                    id={elem._id}
                    key={`${elem._id}-${index + 1}`}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      margin: "0 auto",
                    }}
                  >
                    <img
                      src={elem[`${tradIdentifier}_preview`]}
                      alt="preview element 2"
                      style={{
                        width: "30px",
                        height: "30px",
                        marginRight: "2rem",
                      }}
                    />
                    <Typography variant="body2" sx={{ width: "100%" }}>
                      {elem.title}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </div>
    );
  } else return <Skeleton height={70} />;
};
